<template>
    <div
        :id="id"
        ref="container"
        :data-testid="testId || 'input-dropdown-wrapper'"
        class="tl-input-dropdown-wrapper"
    >
        <FloatLabel
            :id="id"
            :label="label"
        >
            <div
                class="tl-input-dropdown"
                :class="{
                    'tl-input-filled': filled,
                    'tl-input-disabled': disabled,
                    'tl-input-focus': dropdownOpen,
                    'tl-input-invalid': !!error,
                    'tl-input-multiline': multiline
                }"
                role="combobox"
                aria-haspopup="listbox"
                :aria-expanded="dropdownOpen"
                :aria-owns="`${id}-listbox`"
                :aria-controls="`${id}-listbox`"
                :aria-disabled="disabled"
                :aria-invalid="!!error"
                :aria-labelledby="`${id}-label`"
                :tabindex="!disabled ? 0 : -1"
                @click="handleContainerClick(false)"
                @keydown="handleKeyDown"
                @blur="handleBlur"
            >
                <span
                    class="tl-input-dropdown-label"
                    role="textbox"
                    :aria-readonly="true"
                    :aria-label="getFilledLabel || label"
                    :title="getFilledLabel || label"
                >
                    <template v-if="showRemovableChips">
                        <div
                            class="tl-input-dropdown-chips"
                            :class="{ 'tl-input-dropdown-chips-multiline': multiline }"
                        >
                            <span
                                v-for="item in (model as InputDropdownItem[])"
                                :key="itemValue(item)"
                                class="tl-input-dropdown-chip"
                                :class="{ 'tl-input-dropdown-chip-multiline': multiline }"
                            >
                                <Words :oneline="!multiline">{{ itemLabel(item) }}</Words>
                                <i
                                    class="tl-input-dropdown-chip-remove mi-close"
                                    @click.stop="handleRemoveItem(item)"
                                />
                            </span>
                        </div>
                    </template>
                    <slot
                        v-else-if="getValue"
                        name="dropdown-selected-option"
                        :selected-item-label="getFilledLabel"
                        :selected-item-value="getValue"
                    >
                        <div class="tl-input-dropdown-option-label">
                            <Words :oneline="!multiline">
                                {{ getFilledLabel || '&nbsp;' }}
                            </Words>
                        </div>
                    </slot>
                    <div
                        v-else
                        class="tl-input-dropdown-placeholder"
                    >
                        <Words :oneline="!multiline">
                            {{ placeholder || '&nbsp;' }}
                        </Words>
                    </div>
                </span>

                <i
                    v-if="filled && !disabled && !required"
                    class="tl-input-dropdown-clear-icon mi-close"
                    @click.stop="handleClear"
                />
                <span
                    v-if="optionsLoading"
                    class="tl-input-dropdown-icon"
                >
                    <LoadingCircle />
                </span>
                <i
                    v-else
                    class="tl-input-dropdown-icon mi-chevron-down"
                />
            </div>
        </FloatLabel>

        <Popover
            ref="popoverComponentRef"
            v-model="dropdownOpen"
            :reference="container"
            :offset="3"
            match-reference-width
            :max-height="DROPDOWN_MAX_HEIGHT"
        >
            <div
                :id="`${id}-listbox`"
                class="tl-input-dropdown-overlay"
                :aria-hidden="!dropdownOpen"
            >
                <label
                    v-if="isSearchable"
                    class="tl-input-dropdown-search tl-input-dropdown-option"
                >
                    <input
                        ref="searchRef"
                        v-model="query"
                        type="search"
                        :placeholder="t('search')"
                        role="combobox"
                        aria-autocomplete="list"
                        :aria-controls="`${id}-listbox`"
                        @input="onSearchChange"
                        @keydown="handleKeyDown"
                    />
                </label>
                <div
                    class="tl-input-dropdown-list-container"
                    tabindex="-1"
                >
                    <ul
                        ref="listbox"
                        class="tl-input-dropdown-list"
                        role="list"
                    >
                        <li
                            v-for="(item, index) in filteredItems"
                            :key="item[keyField ? keyField : 'value']"
                            class="tl-input-dropdown-option"
                            :class="{
                                'tl-input-option-selected': isActive(item),
                                'tl-input-option-multiline': multiline,
                                'tl-input-option-highlighted': highlightedOptionIndex === index
                            }"
                            data-testid="input-dropdown-options-item"
                            @click.prevent="handleSelect(item)"
                            @mousemove="handleOptionMouseOver(index)"
                        >
                            <slot
                                name="dropdown-option"
                                :item="item"
                            >
                                <Position
                                    v-if="checkmark"
                                    x="center"
                                    y="center"
                                    class="tl-input-dropdown-checkmark"
                                >
                                    <CheckTrigger :model-value="isActive(item)" />
                                </Position>
                                <Words
                                    :oneline="!multiline"
                                    class="text-break"
                                >{{ itemLabel ? itemLabel(item) : item }}</Words>
                            </slot>
                        </li>
                    </ul>
                </div>
            </div>
        </Popover>

        <ErrorText
            v-if="error && !dropdownOpen"
            :error="error"
        />
    </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch, computed, nextTick, useId } from 'vue'
import ErrorText from 'widget-components/components/ErrorText/ErrorText.vue'
import { promiseTimeout, until } from '@vueuse/core'
import { useI18n } from 'widget-components/utilities/i18n'
import { clamp, isNil } from 'ramda'
import { clone } from 'widget-components/utilities/clone'
import equals from 'fast-deep-equal/es6'
import Position from '../Position/Position.vue'
import CheckTrigger from '../InputCheckbox/CheckTrigger.vue'
import type { InputDropdownItem, InputDropdownProps } from './types'
import Words from 'widget-components/components/Words/Words.vue'
import FloatLabel from '../FloatLabel/FloatLabel.vue'
import LoadingCircle from '../LoadingCircle/LoadingCircle.vue'
import Popover from 'widget-components/atoms/Popover/Popover.vue'

const DROPDOWN_MAX_HEIGHT = 400

const container = ref<HTMLDivElement>()
const popoverComponentRef = ref<InstanceType<typeof Popover>>()
const searchRef = ref<HTMLInputElement>()
const id = computed(() => {
    return props.id ? `${props.id}-input` : useId()
})
const highlightedOptionIndex = ref<number>(-1)
const listbox = ref<HTMLUListElement>()

defineOptions({ inheritAttrs: false })
const props = defineProps<InputDropdownProps & {
    required?: boolean
    showChips?: boolean
}>()

const emit = defineEmits(['update:modelValue', 'clear', 'dropdown-open'])

const query = ref('')
const dropdownOpen = ref(false)
const model = ref<InputDropdownProps['modelValue']>(props.multiple ? [] : undefined)

const filled = computed(() => props.multiple ? model.value?.length : !!model.value)

const isSearchable = computed(() => {
    return (props.items || []).length > 5
})

const { t } = useI18n()

const sortItems = (a: InputDropdownItem, b: InputDropdownItem, searchQuery?: string) => {
    if (searchQuery) {
        const labelA = props.itemLabel(a).toLowerCase()
        const labelB = props.itemLabel(b).toLowerCase()
        const startsWithA = labelA.startsWith(searchQuery)
        const startsWithB = labelB.startsWith(searchQuery)

        // Prioritize items that start with the query
        if (startsWithA && !startsWithB) return -1
        if (!startsWithA && startsWithB) return 1
    }

    // Alphabetical sort
    return props.itemLabel(a).localeCompare(props.itemLabel(b))
}

const filteredItems = computed(() => {
    const items = props.items ?? []

    if (isSearchable.value && query.value) {
        const q = query.value.toLowerCase()

        return items
            .filter((el) => {
                const label = props.itemLabel(el).toLowerCase()
                const value = props.itemValue(el).toLowerCase()
                return label.includes(q) || value.includes(q)
            })
            .sort((a, b) => sortItems(a, b, q))
    }

    return items
})

const selectedOptions = computed(() => {
    if (props.multiple) {
        return [...(model.value as InputDropdownItem[]).map((el) => props.itemLabel(el))].join(', ')
    }
    return model.value ? props.itemLabel(model.value) : ''
})

const selectedOptionsValue = computed(() => {
    if (props.multiple) {
        return [...(model.value as InputDropdownItem[]).map((el) => props.itemValue(el))].join(', ')
    }
    return model.value ? props.itemValue(model.value) : ''
})

const getFilledLabel = computed(() => {
    if (props.multiple) {
        return model.value?.length ? selectedOptions.value : ''
    }

    return model.value ? selectedOptions.value : ''
})

const getValue = computed(() => {
    if (props.multiple) {
        return model.value?.length ? selectedOptionsValue.value : ''
    }

    return model.value ? selectedOptionsValue.value : ''
})

const showRemovableChips = computed(() => {
    return props.multiple && props.showChips && model.value?.length
})

const isActive = (item: InputDropdownItem) => {
    if (props.multiple) {
        return !!(model.value as InputDropdownItem[]).find((el) => props.itemValue(el) === props.itemValue(item))
    }

    return model.value && props.itemValue(model.value) === props.itemValue(item)
}

const handleMultiselect = (item: InputDropdownItem) => {
    const exists = (model.value as InputDropdownItem[]).find((el) => props.itemValue(el) === props.itemValue(item))

    if (exists) {
        model.value = (model.value as InputDropdownItem[]).filter((el) => props.itemValue(el) !== props.itemValue(item))
    } else {
        model.value.push(item)
    }
}

const handleRemoveItem = (item: InputDropdownItem) => {
    model.value = (model.value as InputDropdownItem[]).filter((el) => props.itemValue(el) !== props.itemValue(item))
}

const handleSelect = (item: InputDropdownItem, autoClose = true) => {
    if (props.multiple) {
        handleMultiselect(item)
        popoverComponentRef.value?.update()
        focusContainer()
    } else {
        model.value = item
        if (autoClose) {
            handleDropdownClose()
        }
    }
}

const toggleDropdownVisibility = (value?: boolean) => {
    dropdownOpen.value = value ?? !dropdownOpen.value

    if (dropdownOpen.value) {
        emit('dropdown-open')
    }
}

const findPrevOptionIndex = (index: number) => {
    return index > 0 ? index - 1 : index
}

const findNextOptionIndex = (index: number) => {
    const lastIndex = Math.max(filteredItems.value.length - 1, 0)
    const val = index !== lastIndex ? index + 1 : index

    return clamp(0, lastIndex, val)
}

const updateHighlightedOption = (index: number) => {
    highlightedOptionIndex.value = index

    if (listbox.value) {
        nextTick(() => {
            listbox.value?.children[index]?.scrollIntoView({ block: 'nearest', inline: 'start' })
        })
    }
}

const handleDropdownOpen = () => {
    if (props.disabled) {
        return
    }

    toggleDropdownVisibility(true)
}

const handleContainerClick = (focus?: boolean) => {
    if (!dropdownOpen.value) {
        handleDropdownOpen()
    } else {
        handleDropdownClose(undefined, undefined, focus)
    }
}

const handleArrowDownKey = () => {
    if (!dropdownOpen.value) {
        handleDropdownOpen()
    } else {
        updateHighlightedOption(findNextOptionIndex(highlightedOptionIndex.value))
    }
}

const handleArrowUpKey = () => {
    if (!dropdownOpen.value) {
        handleDropdownOpen()
    } else {
        if (highlightedOptionIndex.value === -1) {
            highlightedOptionIndex.value = 0
        } else {
            updateHighlightedOption(findPrevOptionIndex(highlightedOptionIndex.value))
        }
    }
}

const handleEnterKey = () => {
    if (!dropdownOpen.value) {
        handleDropdownOpen()
    } else {
        if (highlightedOptionIndex.value !== -1) {
            const newValue = filteredItems.value[highlightedOptionIndex.value]
            if (newValue) {
                handleSelect(newValue, false)
            }
        }
        if (!props.multiple) {
            handleDropdownClose(undefined, undefined, true)
        }
    }
}

const handleSpaceKey = (event: KeyboardEvent) => {
    if (!document.activeElement?.contains(searchRef.value!)) {
        event.preventDefault()
        handleContainerClick()
    }
}

const handleEscapeKey = () => {
    handleDropdownClose()
}

const handleKeyDown = (event: KeyboardEvent) => {
    if (props.disabled) {
        event.preventDefault()
        return
    }

    switch (event.code) {
        case 'Space':
            handleSpaceKey(event)
            break

        case 'Enter':
            handleEnterKey()
            event.preventDefault()
            break

        case 'ArrowDown':
            handleArrowDownKey()
            event.preventDefault()
            break

        case 'ArrowUp':
            handleArrowUpKey()
            event.preventDefault()
            break

        case 'Escape':
            handleEscapeKey()
            event.preventDefault()
            break

        default:
            break
    }
}

const handleOptionMouseOver = (index: number) => {
    highlightedOptionIndex.value = index
}

const handleDropdownClose = (event?: Event, stop?: boolean, focus?: boolean) => {
    if (!dropdownOpen.value) {
        return
    }

    if (stop) {
        event?.stopPropagation()
    }

    dropdownOpen.value = false
    query.value = ''
    highlightedOptionIndex.value = -1

    // to keep focus of element, mostly for key handling
    if (focus) {
        nextTick(focusContainer)
    }
}

const handleBlur = async (event: FocusEvent) => {
    // wait for
    await promiseTimeout(100)

    const relatedTarget = event.relatedTarget as HTMLElement | null

    const dropdownOverlay = popoverComponentRef.value?.floatingRef
    if (relatedTarget && (
        dropdownOverlay?.contains(relatedTarget)
        || searchRef.value && relatedTarget === searchRef.value
        || container.value?.contains(relatedTarget)
    )) {
        return
    }

    // Close the dropdown when focus truly leaves
    handleDropdownClose()
}

const focusContainer = () => {
    container.value?.querySelector<HTMLElement>('.tl-input-dropdown')?.focus()
}

const handleClear = () => {
    if (props.disabled) {
        return
    }

    model.value = props.multiple ? [] : null
    emit('clear')
    popoverComponentRef.value?.update()
}

const onSearchChange = () => {
    highlightedOptionIndex.value = -1
}

const handleMultipleValue = (values: typeof props.modelValue) => {
    const newValues = (Array.isArray(values) ? values :
        [values]).map(i => props.itemValue(i))
    const items = props.items?.filter((el) => newValues.includes(props.itemValue(el)))
    model.value = clone(items)
}

const handleInitialValue = () => {
    if (isNil(props.initialValue)) {
        return
    }

    if (props.multiple) {
        handleMultipleValue(props.initialValue)
    } else {
        const initialValue = props.itemValue(props.initialValue)
        const item = props.items?.find((el) => props.itemValue(el) === initialValue)
        model.value = clone(item) || props.label
    }
}

const focusSearch = () => {
    if (isSearchable.value) {
        searchRef.value?.focus()
    }
}

onMounted(() => {
    handleInitialValue()
})

watch(dropdownOpen, (open) => {
    if (open) {
        nextTick(async () => {
            const isPositioned = computed(() => popoverComponentRef.value?.isPositioned)
            await until(isPositioned).toBeTruthy()
            focusSearch()
        })
    }
})

watch(() => props.initialValue, (val, prev) => {
    if (!equals(val, prev)) {
        handleInitialValue()
    }
})

watch(model, (val) => {
    emit('update:modelValue', val)
}, { deep: true })

watch(() => filteredItems.value.length, () => {
    popoverComponentRef.value?.update()
})

watch(() => props.modelValue, () => {
    if (!equals(props.modelValue, model.value)) {
        if (props.multiple) {
            handleMultipleValue(props.modelValue)
        } else {
            if (isNil(props.modelValue)) {
                model.value = props.modelValue
            } else {
                handleSelect(props.modelValue)
            }
        }
    }
}, { deep: true })

defineExpose({
    updateDropdownOverlay: () => popoverComponentRef.value?.update(),
    focusSearch,
})
</script>

<style lang="sass" scoped>
@import './InputDropdown.sass'
</style>
